<!--
  NavRail.vue — the dark left navigation rail (shell chrome for the authenticated app).
  Purpose: reproduce the go.lacak.io left rail: brand, the signed-in user block, the
  primary product nav (Overview / Tracking / Geo links / Reports / Fleet management /
  Field service) and the secondary utility nav (Alerts + badge / Messages / Device
  activation / Devices and settings / Information center), matching icons + order.
  Routes point at this scaffold's real routes where they exist (Tracking -> '/',
  Devices and settings -> '/devices'); the rest are layout-faithful placeholders.
  Reconstructed from the deployed go.lacak.io /#/online/trackers shell; labels/icons
  verbatim from the live DOM, internal impl re-authored.
-->
<template>
  <nav class="navrail" :class="{ collapsed }">
    <div class="navrail-top">
      <button class="navrail-icon-btn" @click="collapsed = !collapsed" v-tip="'Toggle menu'">
        <i class="material-icons">menu_open</i>
      </button>
      <div class="navrail-brand" v-show="!collapsed">
        <span class="brand-mark">◎</span><span class="brand-text">Lacak<span class="brand-dot">.io</span></span>
      </div>
    </div>

    <div class="navrail-user">
      <i class="material-icons user-avatar">account_circle</i>
      <div class="user-meta" v-show="!collapsed">
        <div class="user-name">{{ userTitle || 'Operator' }}</div>
        <div class="user-id">ID: {{ userId != null ? userId : '—' }}</div>
      </div>
    </div>

    <ul class="navrail-list nav-primary">
      <li v-for="item in primary" :key="item.label">
        <a
          class="navrail-item"
          :class="{ active: isActive(item) }"
          @click="go(item)"
        >
          <i class="material-icons">{{ item.icon }}</i>
          <span class="navrail-label" v-show="!collapsed">{{ item.label }}</span>
        </a>
      </li>
    </ul>

    <div class="navrail-spacer"></div>

    <ul class="navrail-list nav-secondary">
      <li v-for="item in secondary" :key="item.label">
        <a class="navrail-item" :class="{ active: isActive(item) }" @click="go(item)">
          <i class="material-icons">{{ item.icon }}</i>
          <span class="navrail-label" v-show="!collapsed">{{ item.label }}</span>
          <span v-if="item.badge && !collapsed" class="navrail-badge">{{ item.badge }}</span>
        </a>
      </li>
      <li>
        <a class="navrail-item" @click="logout">
          <i class="material-icons">logout</i>
          <span class="navrail-label" v-show="!collapsed">Log out</span>
        </a>
      </li>
    </ul>
  </nav>
</template>

<script>
import { clearSessionLocal } from '@/shell/session'

export default {
  name: 'NavRail',
  data() {
    return {
      collapsed: false,
      primary: [
        { label: 'Overview', icon: 'dashboard', route: null },
        { label: 'Tracking', icon: 'map', route: '/' },
        { label: 'Geo links', icon: 'share', route: null },
        { label: 'Reports', icon: 'assessment', route: null },
        { label: 'Fleet management', icon: 'directions_car', route: null },
        { label: 'Field service', icon: 'groups', route: null }
      ],
      secondary: [
        { label: 'Alerts', icon: 'notifications', route: null, badge: '99+' },
        { label: 'Messages', icon: 'textsms', route: null },
        { label: 'Device activation', icon: 'add_box', route: null },
        { label: 'Devices and settings', icon: 'settings', route: '/devices' },
        { label: 'Information center', icon: 'help_outline', route: null }
      ]
    }
  },
  computed: {
    userTitle() {
      const g = this.$store && this.$store.getters
      return g && Object.prototype.hasOwnProperty.call(g, 'user/title') ? g['user/title'] : ''
    },
    userId() {
      const g = this.$store && this.$store.getters
      return g && Object.prototype.hasOwnProperty.call(g, 'user/userId') ? g['user/userId'] : null
    }
  },
  methods: {
    isActive(item) {
      return item.route && this.$route.path === item.route
    },
    go(item) {
      if (item.route && this.$route.path !== item.route) this.$router.push(item.route)
      // placeholder items (route:null) are layout-faithful only — no-op
    },
    async logout() {
      // Dispatch the store logout if it's registered; either way always clear the local
      // session + redirect (dispatching an unregistered action is a no-op that never throws,
      // so a bare try/catch fallback would silently do nothing).
      const registered = this.$store._actions && this.$store._actions['user/logOut']
      if (registered) { try { await this.$store.dispatch('user/logOut', { noServer: false }) } catch (e) { /* fall through to local cleanup */ } }
      clearSessionLocal()
      if (this.$route.name !== 'login') this.$router.push({ name: 'login' })
    }
  }
}
</script>

<style scoped>
.navrail {
  width: 232px;
  min-width: 232px;
  height: 100%;
  background: #001e32;
  color: #b8c4d0;
  display: flex;
  flex-direction: column;
  font-family: Roboto, 'Helvetica Neue', Arial, sans-serif;
  transition: width 0.15s ease, min-width 0.15s ease;
  overflow: hidden;
}
.navrail.collapsed {
  width: 60px;
  min-width: 60px;
}
.navrail-top {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 12px 10px;
}
.navrail-icon-btn {
  background: none;
  border: none;
  color: #8aa0b4;
  cursor: pointer;
  padding: 4px;
  display: flex;
}
.navrail-brand {
  display: flex;
  align-items: center;
  font-size: 20px;
  font-weight: 500;
  color: #e8eef4;
}
.brand-mark { color: #17b3c4; margin-right: 6px; font-size: 22px; }
.brand-dot { color: #17b3c4; }
.navrail-user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px 14px;
}
.user-avatar { font-size: 30px; color: #6f8698; }
.user-name { color: #e8eef4; font-size: 14px; font-weight: 500; line-height: 1.2; }
.user-id { color: #6f8698; font-size: 12px; }
.navrail-list { list-style: none; margin: 0; padding: 0; }
/* renamed from .primary/.secondary — those collide with Vuetify's global theme-colour utility
   classes (.primary/.secondary set a blue/grey background), which was tinting the nav sections. */
.navrail-list.nav-primary { padding-top: 4px; }
.navrail-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 11px 18px;
  cursor: pointer;
  font-size: 14px;
  color: #b8c4d0;
  border: 1px solid transparent;      /* reserve space so the active outline doesn't shift items */
  border-radius: 8px;
  user-select: none;
}
.navrail-item:hover { background: rgba(255, 255, 255, 0.04); color: #e8eef4; }
.navrail-item .material-icons { font-size: 20px; }
/* active = a blue outlined rounded rectangle with blue text/icon, no fill (real go.lacak.io) */
.navrail-item.active {
  color: #1e96dc;
  font-weight: 500;
  background: transparent;
  border-color: #1e96dc;
}
.navrail-label { flex: 1; }
.navrail-badge {
  background: #e53935;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  border-radius: 10px;
  padding: 1px 7px;
}
.navrail-spacer { flex: 1; }
.navrail-list.nav-secondary { padding-bottom: 10px; }
</style>
